import { faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Fragment, ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { useStoreItemContext } from '../contexts/store-item.context'
import { useStoreGate } from '../hooks/useStoreGate'
import { Checkout } from './Checkout'
import { Dialog } from './Dialog'
import { ErbBadge } from './ErbBadge'
import { Price } from './Price'
import { Button } from './ui/Button'

const ItemCardPlaceholder = () => {
    return <div className='border border-white/10 rounded bg-white/5 min-h-96 hidden md:block'></div>
}

const ItemRowPlaceholder = () => {
    return <div className='border border-white/10 rounded bg-white/5 h-[4.75rem]'></div>
}

/**
 * The checkout modal, and the `Dialog` that owns it.
 *
 * Lifted out of `ItemCard` unchanged when the list view arrived, so both the card and the row hand
 * the *same* trigger to the *same* dialog. `Dialog.Trigger` clones its single child to attach an
 * `onClick`, and nothing below the trigger knows or cares what that child looks like — which is
 * what makes a second presentation of an item free, and what keeps the purchase flow out of the
 * blast radius of a layout change.
 */
const ItemDialog = ({ children }: { children: ReactNode }) => {
    const { item, state } = useStoreItemContext()
    const { name, category, media_set } = item
    const image = media_set?.[0]
    // Whether this account may buy at all: a freeze, a store block, the trust gate or the
    // integrity gate. Gated here rather than in each view for the same reason the dialog was
    // lifted: the card and the row hand over the same trigger, so one check covers both and
    // cannot drift. The order endpoint refuses it regardless; stopping at the trigger means the
    // player is not walked through three checkout steps to be told no at the end.
    //
    // Until this read the whole reason, it read only the freeze, and the other three gates
    // refused into a client that reported the refusal as a completed purchase.
    const { blockedReason } = useStoreGate()

    return (
        <Dialog>
            <Dialog.Trigger disabled={state === 'purchased' || !!blockedReason}>{children}</Dialog.Trigger>
            <Dialog.Content>
                <Checkout>
                    <div className='leading-relaxed tracking-wide select-none'>
                        {/* Sticky, because the panel now scrolls: on a phone the checkout is
                            taller than the screen, and the X has to stay reachable from
                            wherever the shopper has scrolled to. */}
                        <header className='sticky top-0 z-10 grid grid-cols-[1fr_auto] font-bold border-b border-white/20 bg-black p-4 gap-x-4 items-center'>
                            <div className='whitespace-nowrap overflow-hidden overflow-ellipsis'>{name}</div>
                            <Dialog.Close />
                            <div className='text-[0.625rem] text-white/50 uppercase'>{category}</div>
                        </header>
                        <div className='border-b border-white/20'>
                            <Checkout.Progress />
                        </div>
                        <div className='flex flex-row flex-wrap items-start p-4 gap-4'>
                            <div className='min-w-56 max-w-96 xl:w-96 basis-1/3 grow rounded overflow-hidden bg-white/5 p-1'>
                                <img src={image} />
                            </div>
                            <div className='min-w-56 max-w-96 xl:w-96 basis-1/3 flex flex-col gap-4 grow'>
                                <div className='rounded bg-white/5 p-4 flex flex-col gap-4'>
                                    <Checkout.Header />
                                    <Checkout.Form />
                                </div>
                            </div>
                        </div>
                    </div>
                </Checkout>
            </Dialog.Content>
        </Dialog>
    )
}

/** Every price the listing carries, joined by `OR`. Shared by both views. */
const Prices = ({ className }: { className?: string }) => {
    const { item } = useStoreItemContext()
    return (
        <div className={twMerge('flex flex-row items-center justify-center gap-2', className)}>
            {item.price_set.map((p, i) => (
                <Fragment key={p.currency}>
                    <Price price={p} />
                    {i < item.price_set.length - 1 && <div className='text-[0.625rem] text-white/50'>OR</div>}
                </Fragment>
            ))}
        </div>
    )
}

const ItemCard = () => {
    const { item, state } = useStoreItemContext()
    const { name, category, media_set, erb } = item
    const image = media_set?.[0]
    const { blockedReason } = useStoreGate()

    const isPurchased = state === 'purchased'

    return (
        <ItemDialog>
            <div className='relative border border-white/30 hover:border-white/50 rounded overflow-hidden flex flex-col hover:cursor-pointer group'>
                <div className='relative overflow-hidden h-full'>
                    <img src={image} className='w-full h-full object-cover object-top' style={{ aspectRatio: '1/1' }} />
                    {/* Top-right, over the artwork — the same corner and the same pill the
                        collection banners use for their item counts. */}
                    <div className='absolute top-0 left-0 right-0 p-3 flex flex-row justify-end'>
                        <ErbBadge value={erb} variant='overlay' />
                    </div>
                    <div className='absolute bottom-0 left-0 right-0 whitespace-nowrap text-center font-bold bg-black/80'>
                        <div className='p-2 overflow-hidden overflow-ellipsis' title={name}>
                            {name}
                        </div>
                        <div className='p-1 text-[0.625rem] text-white/50 uppercase bg-black/50'>{category}</div>
                    </div>
                </div>
                <Prices className='p-2' />
                <Button disabled={!!blockedReason} className='m-1' title={blockedReason}>
                    BUY
                </Button>
                {blockedReason && (
                    <div className='px-2 pb-2 text-[0.625rem] text-center text-amber-400/80'>{blockedReason}</div>
                )}
                {isPurchased && (
                    <div className='absolute inset-0 bg-black/90 flex flex-col gap-4 items-center justify-center'>
                        <FontAwesomeIcon icon={faCircleCheck} className='text-2xl text-green-500' />
                        <div className='text-2xs uppercase'>Item purchased</div>
                        <div className='text-2xs uppercase'>Web3 asset delivering</div>
                    </div>
                )}
            </div>
        </ItemDialog>
    )
}

/**
 * The list-view counterpart to `ItemCard`.
 *
 * The card is square and image-led: at 375px it is one ~370px tile per screen-and-a-bit, so a
 * fifty-item collection is a very long scroll for a shopper who already knows what a loot box
 * looks like. This row is 76px, and it keeps everything the card states — thumbnail, name,
 * category, ERB, every price, and the same BUY control.
 *
 * It is not a second way to buy: it renders the identical `ItemDialog`, so the click opens the
 * identical checkout.
 */
const ItemRow = () => {
    const { item, state } = useStoreItemContext()
    const { name, category, media_set, erb } = item
    const image = media_set?.[0]
    const { blockedReason } = useStoreGate()

    const isPurchased = state === 'purchased'

    return (
        <ItemDialog>
            <div
                className={twMerge(
                    'group relative flex flex-row items-center gap-3 rounded border border-white/20 p-2',
                    'hover:border-white/50 hover:cursor-pointer',
                    isPurchased && 'hover:cursor-default'
                )}
            >
                {/* 48px on a phone, 56 from `sm`. Those 8px go to the name, and they are the
                    difference between `BR1 Character #11235` fitting on one line and wrapping. */}
                <img
                    src={image}
                    alt=''
                    className='h-12 w-12 shrink-0 rounded object-cover object-top bg-white/5 sm:h-14 sm:w-14'
                />

                {/*
                  Stacked on a phone, one line from `sm`. The fixed `sm:w-*` slots are what make
                  the ERB and the price line up down the list instead of each row finding its own
                  position — a column of numbers you cannot compare down the page is most of what
                  a list view is for.
                */}
                <div className='flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-1.5'>
                    {/* `text-sm`, and allowed a second line on a phone.

                        At the card's inherited 16px, `BR1 Character #11235` wants 207px in the
                        179 a 375px row leaves, so it ellipsized away the ID — the only thing
                        telling one row from the next. At 14px, with the smaller thumbnail, it
                        fits on one line with a few pixels to spare; `line-clamp-2` is what
                        happens to a name longer than that, and it wraps at a space rather than
                        losing its tail. */}
                    <span
                        // `flex-auto`, not `flex-1`. The difference is the flex *basis*: `flex-1`
                        // is `0%`, so the name's hypothetical width is zero, it never triggers a
                        // wrap, and the fixed slots beside it simply take everything — at 768 with
                        // the filter panel open the row is 400px, the slots want 320, and the name
                        // was laid out at literally 0px wide. With an `auto` basis the row wraps
                        // the slots underneath instead, which is the phone layout and correct.
                        className='line-clamp-2 min-w-0 flex-auto text-sm font-bold sm:line-clamp-1 sm:text-base'
                        title={name}
                    >
                        {name}
                    </span>

                    {/* Category, ERB and price share the phone's second line and hold fixed slots
                        from `sm`. Putting the category here rather than under the name is what
                        keeps a phone row at 80px instead of 98 — and on a desktop it turns three
                        loose facts into three columns you can read straight down the page. */}
                    <div className='flex flex-row items-center gap-3 sm:ml-auto sm:shrink-0 sm:gap-4'>
                        <span className='min-w-0 flex-1 truncate text-[0.625rem] uppercase text-white/50 sm:w-20 sm:flex-none sm:text-right'>
                            {category}
                        </span>
                        {/* The slot holds its width from `sm` even when the item has no ERB, so a
                            weapon and a loot box still line their prices up. Below `sm` it
                            collapses entirely rather than leaving a gap. */}
                        <div className={twMerge('hidden sm:flex sm:w-24 sm:justify-end', erb && 'flex')}>
                            <ErbBadge value={erb} />
                        </div>
                        <Prices className='sm:w-28 sm:justify-end' />
                    </div>
                </div>

                {/* No reason text here: the row is 76px and has no space for it. The disabled
                    button carries it as a title, and the card view states it in full. */}
                <Button disabled={!!blockedReason} className='shrink-0 px-4 py-2 text-sm' title={blockedReason}>
                    BUY
                </Button>

                {isPurchased && (
                    <div className='absolute inset-0 flex flex-row items-center justify-center gap-3 rounded bg-black/90'>
                        <FontAwesomeIcon icon={faCircleCheck} className='text-green-500' />
                        <div className='text-2xs uppercase'>Item purchased — Web3 asset delivering</div>
                    </div>
                )}
            </div>
        </ItemDialog>
    )
}

export const Item = () => {}

Item.Card = ItemCard
Item.CardPlaceholder = ItemCardPlaceholder
Item.Row = ItemRow
Item.RowPlaceholder = ItemRowPlaceholder
